/**
 * Auto-dismissing notification banner.
 *
 * Subscribes to the module-level event emitter (notifications.ts) so that
 * both React components and non-React code (HTTP interceptors) can trigger
 * user-visible messages. Renders through the shared {@link Banner} (card
 * variant) so its layout, action button, and dismiss placement match every
 * other banner; this file only owns the fixed toast positioning, the
 * slide-in animation, and the auto-dismiss timers.
 */

import { useCallback, useEffect, useState } from 'react'

import { useUiProviders } from '../hooks/useUiProviders.js'
import { type NotificationAction, type NotificationType, onNotification } from '../notifications.js'
import { Banner } from './Banner.js'

interface Notification {
  id: number
  message: string
  type: NotificationType
  action?: NotificationAction
}

let nextId = 0

const DISMISS_MS: Record<NotificationType, number> = {
  error: 8_000,
  warning: 6_000,
  info: 4_000,
}

export function NotificationBanner() {
  const [notification, setNotification] = useState<Notification | null>(null)

  // Subscribe to the module-level emitter
  useEffect(() => {
    return onNotification((message, type, action) => {
      const id = ++nextId
      setNotification({ id, message, type, action })
    })
  }, [])

  // Auto-dismiss timer — notifications with actions persist until manually dismissed
  useEffect(() => {
    if (!notification || notification.action) return
    const timer = setTimeout(() => {
      setNotification((current) => (current?.id === notification.id ? null : current))
    }, DISMISS_MS[notification.type])
    return () => clearTimeout(timer)
  }, [notification])

  const dismiss = useCallback(() => setNotification(null), [])

  // Mounted on EVERY route, including the landing page where the ClassMap
  // bonds are deliberately not in the entry — rendering Banner un-wired threw
  // "No UIClassMap has been set" and crashed the page. Hold the toast until
  // the hook has loaded the bonds (auto-dismiss timers above keep running, so
  // a stale toast can still expire while loading — acceptable for a toast).
  const uiReady = useUiProviders(notification !== null)

  if (!notification || !uiReady) return null

  return (
    // The wrapper owns toast positioning + the slide-in; Banner owns the look.
    <div
      style={{
        position: 'fixed',
        // Clears the workspace's mobile bottom nav when it's mounted (the nav
        // publishes --mol-fixed-bottom-offset), plus a breathing-room margin.
        bottom: 'calc(var(--mol-fixed-bottom-offset, 0px) + 40px)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10000,
        maxWidth: 560,
        width: 'calc(100% - 32px)',
        animation: 'notif-slide-in 200ms ease-out',
      }}
    >
      <Banner
        tone={notification.type}
        variant="card"
        role="alert"
        molId={`notification-banner-${notification.type}`}
        message={notification.message}
        actions={
          notification.action
            ? [
                {
                  label: notification.action.label,
                  href: notification.action.href,
                  external: notification.action.external,
                  color: 'primary',
                  molId: 'notification-banner-action',
                },
              ]
            : undefined
        }
        onDismiss={dismiss}
        dismissMolId="notification-banner-dismiss"
        style={{ backdropFilter: 'blur(8px)' }}
      />
      <style>{`@keyframes notif-slide-in { from { opacity: 0; transform: translateX(-50%) translateY(8px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }`}</style>
    </div>
  )
}
